import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';

import { ApiError } from '@shared/api/client';
import { resolveErrorCopy } from '@shared/errors/catalog';
import { formatMoney } from '@shared/format';
import { Breadcrumb } from '@shared/chrome/Breadcrumb';
import {
  DTable,
  DTableBody,
  DTableHead,
  DTableRowHeader,
  DTableScroll,
  RowSkeleton,
} from '@shared/ui/dtable';
import { ConfirmDialog, type ConfirmSubject } from '@shared/ui/dialog';
import { EmptyState } from '@shared/ui/EmptyState';
import { RowMenu } from '@shared/ui/rowmenu';
import { StateBadge } from '@shared/ui/StateBadge';
import { useToast } from '@shared/ui/toast/ToastProvider';

import { SurfaceError, TruncationLine } from '../_shared/components';
import { sharedCopy } from '../_shared/copy';
import { formatPeriodMonths } from '../_shared/constants';
import type { Plan } from '../_shared/types';
import { copy } from './copy';
import { errors, fieldErrors } from './errors';
import { toPlanWriteBody, type PlanFormValues } from './forms.schema';
import { PlanDialog } from './Plans.PlanDialog';
import { useCreatePlan, useDeletePlan, useUpdatePlan } from './mutations';
import { plansKey, usePlans } from '../_shared/queries';

const TABLE_COLUMNS = 4;

/**
 * `/plans` — the operator-defined subscription products.
 *
 * Four columns and no fifth: the catalog gives a plan a name, a price, a period,
 * and a scope, and the scope is identical on every row because it is a platform
 * setting — so it is stated twice in prose and drawn nowhere as a control or a
 * column.
 *
 * **No rank column and no move handles**, and the contrast with the shelf table
 * is the point: order is data there because the home page is «an ordered list of
 * configured shelves», and nothing orders plans. **No sort control** either — the
 * table has no date column. **No active flag**, because a plan has no state; the
 * gateway record next door carries one precisely because its own row names it.
 *
 * The destructive action is quiet in rest (a menu entry, not a button in the
 * row), loud in confirmation (the dialog names the plan), and the confirmation
 * states what survives it: an entitlement already granted is a record of its own
 * and runs to the end of its period.
 */
export function PlansPage() {
  const query = usePlans();
  const queryClient = useQueryClient();
  const toast = useToast();

  const createPlan = useCreatePlan();
  const updatePlan = useUpdatePlan();
  const deletePlan = useDeletePlan();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Plan | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmSubject, setConfirmSubject] = useState<ConfirmSubject | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Plan | null>(null);

  const plans = query.data?.items ?? [];

  /**
   * The currency word for the dialog's price hint, read off a row rather than
   * held anywhere in this repo. Every row carries the value written from the
   * `CURRENCY` setting when it was created, so the first row is a read of the
   * setting rather than a guess at it.
   */
  const currency = plans.find((plan) => plan.currency)?.currency ?? null;

  function openCreate() {
    setEditing(null);
    setFormError(null);
    setDialogOpen(true);
  }

  function openEdit(plan: Plan) {
    setEditing(plan);
    setFormError(null);
    setDialogOpen(true);
  }

  function askDelete(plan: Plan) {
    setPendingDelete(plan);
    setConfirmSubject({
      heading: copy.confirm.heading(plan.name),
      body: copy.confirm.body,
      commitLabel: copy.confirm.commit,
      intent: 'danger',
    });
  }

  function onWriteFailure(error: unknown) {
    const status = error instanceof ApiError ? error.status : 0;
    const code = error instanceof ApiError ? error.code : undefined;
    const detail = code ? fieldErrors[code] : undefined;
    setFormError(detail ?? resolveErrorCopy(code, status, errors).message);
  }

  function submitPlan(values: PlanFormValues) {
    setFormError(null);

    const body = toPlanWriteBody(values);

    if (editing) {
      updatePlan.mutate(
        { planId: editing.id, body },
        {
          onSuccess: () => {
            setDialogOpen(false);
            toast.show(copy.toast.updated, 'success');
          },
          onError: onWriteFailure,
        },
      );
      return;
    }

    createPlan.mutate(body, {
      onSuccess: () => {
        setDialogOpen(false);
        toast.show(copy.toast.created, 'success');
      },
      onError: onWriteFailure,
    });
  }

  function commitDelete() {
    const plan = pendingDelete;
    if (!plan) return;

    deletePlan.mutate(plan.id, {
      onSuccess: () => {
        setConfirmSubject(null);
        setPendingDelete(null);
        toast.show(copy.toast.deleted, 'success');
      },
      onError: (error) => {
        setConfirmSubject(null);
        setPendingDelete(null);
        const status = error instanceof ApiError ? error.status : 0;
        const code = error instanceof ApiError ? error.code : undefined;
        toast.show(resolveErrorCopy(code, status, errors).message, 'danger');
      },
    });
  }

  return (
    <>
      <Breadcrumb items={[{ label: sharedCopy.crumbs.console, to: '/' }, { label: copy.crumb }]} />

      <div className="mb-6 mt-3 grid gap-stack-tight">
        <h1 className="font-display text-h1 font-bold">{copy.head.title}</h1>
        <p className="mb-0 max-w-measure text-[1.0625rem] text-ink-muted">{copy.head.lede}</p>
      </div>

      <section aria-labelledby="plans-heading">
        <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-2">
          <h2 id="plans-heading" className="font-display text-h2 font-bold">
            {copy.section.heading}
          </h2>

          {/*
            Secondary, not primary: the top bar's create action is this view's
            one primary button, so every committing action inside the content
            column is secondary and every destructive one is danger.
          */}
          <button
            type="button"
            onClick={openCreate}
            className="hit-target ms-auto flex items-center gap-2 rounded-sm border border-border-strong bg-surface-raised px-4 text-sm font-semibold text-ink hover:bg-surface-overlay"
          >
            <PlusGlyph />
            {copy.section.add}
          </button>
        </div>

        {/* Above the table, not beneath it: it explains what the table manages before it is read. */}
        <p className="mb-4 max-w-measure text-xs text-ink-muted">
          <b className="font-semibold text-ink">{copy.contextLead}</b> {copy.contextRest}
        </p>

        {query.isError ? (
          <SurfaceError
            error={query.error}
            catalog={errors}
            onRetry={() => void queryClient.invalidateQueries({ queryKey: plansKey() })}
          />
        ) : null}

        {query.isPending ? (
          <>
            <p role="status" className="sr-only">
              {copy.states.loading}
            </p>
            <PlanTable>
              <RowSkeleton columns={TABLE_COLUMNS} />
            </PlanTable>
          </>
        ) : null}

        {query.data ? (
          plans.length === 0 ? (
            <EmptyState message={copy.states.empty} />
          ) : (
            <>
              <PlanTable>
                {plans.map((plan) => (
                  <tr key={plan.id}>
                    <DTableRowHeader>
                      <span className="block">{plan.name}</span>
                      <span className="mt-1 block text-xs text-ink-muted">{copy.renewalNote}</span>
                    </DTableRowHeader>

                    <td className="whitespace-nowrap">
                      <bdi>{formatMoney(plan.price, { currency: plan.currency })}</bdi>
                    </td>

                    <td>
                      <StateBadge kind="neutral">{formatPeriodMonths(plan.period_months)}</StateBadge>
                    </td>

                    <td className="w-[3.5rem]">
                      <RowMenu
                        label={copy.rowMenu.label(plan.name)}
                        entries={[
                          {
                            key: 'edit',
                            label: copy.rowMenu.edit,
                            onSelect: () => openEdit(plan),
                          },
                          {
                            key: 'delete',
                            label: copy.rowMenu.delete,
                            danger: true,
                            onSelect: () => askDelete(plan),
                          },
                        ]}
                      />
                    </td>
                  </tr>
                ))}
              </PlanTable>

              <TruncationLine shown={plans.length} total={query.data.total}>
                {copy.truncationTail}
              </TruncationLine>
            </>
          )
        ) : null}

        {/* The two routes that open the catalogue, named where the operator is standing. */}
        <p className="mb-0 mt-4 max-w-measure text-xs text-ink-muted">
          {copy.outroLead}{' '}
          <Link to="/users" className="text-accent-text">
            {copy.outroUsersLink}
          </Link>
          {copy.outroMiddle}{' '}
          <Link to="/payment-gateways" className="text-accent-text">
            {copy.outroGatewaysLink}
          </Link>
        </p>
      </section>

      <PlanDialog
        open={dialogOpen}
        plan={editing}
        currency={currency}
        busy={createPlan.isPending || updatePlan.isPending}
        formError={formError}
        onSubmit={submitPlan}
        onCancel={() => setDialogOpen(false)}
      />

      <ConfirmDialog
        open={confirmSubject !== null}
        subject={confirmSubject}
        busy={deletePlan.isPending}
        onConfirm={commitDelete}
        onCancel={() => {
          setConfirmSubject(null);
          setPendingDelete(null);
        }}
      />
    </>
  );
}

/** The table frame, shared by the skeleton and the data states so neither reflows the other. */
function PlanTable({ children }: { children: React.ReactNode }) {
  return (
    <DTableScroll>
      <DTable caption={copy.caption} minInlineSize="30rem">
        <DTableHead>
          <tr>
            <th scope="col">{copy.columns.plan}</th>
            <th scope="col">{copy.columns.price}</th>
            <th scope="col">{copy.columns.period}</th>
            <th scope="col">
              <span className="sr-only">{copy.columns.actions}</span>
            </th>
          </tr>
        </DTableHead>
        <DTableBody>{children}</DTableBody>
      </DTable>
    </DTableScroll>
  );
}

function PlusGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="1.125em"
      height="1.125em"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}
